import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import {
  User, Edit2, Save, X, Plus, Camera, Trash2,
  CheckCircle2, Star, Sparkles, BarChart2, Calendar, RefreshCw,
  MessageCircle, Linkedin, Globe, Phone, Mail, ExternalLink, Flame, Gift, Tag, Send,
  Search, Shield, Briefcase, Target, Link as LinkIcon, Building2, Settings as SettingsIcon
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TYPE_LABELS = { cherche: "Je cherche", propose: "Je propose", opportunite: "Opportunité" };

const SECTEURS = [
  "Tech & Digital", "Finance & Investissement", "Immobilier", "Conseil & Management",
  "Commerce, Distribution & E-commerce", "Industrie & Manufacturing", "Santé & Pharma", "Éducation & Formation",
  "Transport & Logistique", "Agriculture & Agroalimentaire", "Médias & Communication", "Énergie & Environnement",
  "Tourisme & Hôtellerie", "Juridique & Comptabilité", "BTP & Construction", "Services aux entreprises", "Autre"
];
const FONCTIONS = [
  "Dirigeant / CEO / Fondateur",
  "Directeur (Commercial, Marketing, Opérations…)",
  "Indépendant / Freelance",
  "Consultant",
  "Investisseur",
  "Autre",
];
const VILLES = ["Casablanca", "Rabat", "Marrakech", "Fès", "Tanger", "Agadir", "Meknès", "Oujda", "Kénitra", "Tétouan", "Autre"];

const FEDERATIONS = [
  "CGEM", "ASMEX", "AMIC", "AMITH", "FENELEC", "FNBTP", 
  "Maroc PME", "CCIS", "CFCIM", "APEBI", "AUSIM", "AMDIE"
];

const BUSINESS_CLUBS = [
  "CDD", "BNI", "CJD", "Réseau Entreprendre Maroc", 
  "Rotary Club", "Lions Club", "French Tech Maroc", "CFCIM Business Club"
];

// ── New networking taxonomies (v2) ─────────────────────────────────────────
const TAILLES_ENTREPRISE = ["Solo", "2–10", "11–50", "51–200", "200+"];
const OBJECTIFS_OPTIONS = [
  "Développer mon réseau",
  "Trouver des clients",
  "Trouver des partenaires",
  "Recruter",
  "Lever des fonds",
  "Trouver des prestataires",
  "Gagner en visibilité",
  "Partager mon expertise",
];
const DISPONIBILITES = [
  { value: "active", label: "Oui, activement" },
  { value: "occasionnelle", label: "Occasionnellement" },
  { value: "indisponible", label: "Pas pour le moment" },
];
const ZONES_ACTIVITE = ["Maroc", "Afrique", "Europe", "International"];
const LANGUES_OPTIONS = ["Arabe", "Français", "Anglais", "Espagnol"];
// Curated, controlled expertises list (used for matching / filters / search)
const EXPERTISES_OPTIONS = [
  "IA", "Marketing", "Vente", "Finance", "Juridique",
  "Recrutement", "SaaS", "Immobilier", "Industrie", "E-commerce",
  "RH", "Export", "Logistique", "Investissement",
  "Stratégie", "Conseil", "Cybersécurité", "Data & Analytics",
  "Design", "Communication", "Levée de fonds", "M&A", "RSE",
  "Tech & Développement", "Comptabilité", "Formation", "Coaching",
  "Achats", "Supply Chain", "Énergie",
];
const MAX_EXPERTISES = 5;

// ── Profile completion scoring (frontend mirror of _completion_pct in
// server.py). Used for live progression while editing. The backend remains
// the official source of truth on save. Keep the two implementations in
// sync if either set of weights changes.
const PROGRESSION_LABEL = (pct) => {
  if (pct < 40) return "Profil à compléter";
  if (pct < 70) return "Profil en construction";
  if (pct < 90) return "Profil visible et exploitable";
  return "Profil optimisé";
};
function computeCompletion(p, { hasPendingPhoto = false } = {}) {
  if (!p) return 0;
  const s = (k) => (typeof p[k] === "string" ? p[k].trim() : "");
  const hasList = (k) => Array.isArray(p[k]) && p[k].length > 0;
  let n = 0;
  // Block 1 — Visibilité annuaire (50)
  if (s("nom_prenom")) n += 8;
  if (s("photo") || hasPendingPhoto) n += 8;
  if (s("fonction")) n += 7;
  if (s("entreprise")) n += 7;
  if (s("secteur")) n += 5;
  if (s("ville")) n += 5;
  if (s("offre").length >= 40) n += 10;
  // Block 2 — Mise en relation (25)
  if (s("phone")) n += 8;
  if (s("linkedin_url")) n += 7;
  if (s("je_peux_aider").length >= 20) n += 5;
  if (s("disponibilite")) n += 5;
  // Block 3 — Matching (15)
  if (hasList("expertises")) n += 6;
  if (hasList("objectifs")) n += 5;
  if (s("zone_activite")) n += 2;
  if (hasList("langues")) n += 2;
  // Block 4 — Confiance (10)
  if (s("references").length >= 15) n += 4;
  if (hasList("federations") || hasList("business_clubs")) n += 3;
  if (s("ice_patente") || p.is_company_verified) n += 3;
  return Math.min(n, 100);
}

// ── Reusable section card (CBM design system, sober & light) ──────────────
function SectionCard({ icon: Icon, title, helper, children, testId }) {
  return (
    <div className="cbm-card p-5 sm:p-6" data-testid={testId}>
      <div className="flex items-start gap-3 mb-5">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ background: "rgba(201,162,39,0.12)", border: "1px solid rgba(201,162,39,0.25)" }}
        >
          <Icon size={15} className="text-gold" />
        </div>
        <div className="min-w-0">
          <h3 className="font-outfit font-semibold text-white text-base leading-tight">{title}</h3>
          {helper && <p className="text-cbm-secondary text-xs mt-1 leading-relaxed">{helper}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

// ── Searchable multiselect with pills (CBM design system) ─────────────────
function MultiSelectPills({ options, value, onChange, placeholder, allowOther, otherValue, onOtherChange, testId, maxItems }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const selected = value || [];
  const atMax = typeof maxItems === "number" && selected.length >= maxItems;
  const filtered = options.filter(o =>
    o.toLowerCase().includes(query.toLowerCase()) && !selected.includes(o)
  );
  const toggle = (item) => {
    if (selected.includes(item)) onChange(selected.filter(s => s !== item));
    else if (!atMax) onChange([...selected, item]);
    setQuery("");
  };
  return (
    <div data-testid={testId}>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map(s => (
            <span key={s}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ background: "rgba(201,162,39,0.12)", border: "1px solid rgba(201,162,39,0.35)", color: "#C9A227" }}
            >
              {s}
              <button type="button" onClick={() => toggle(s)} className="opacity-70 hover:opacity-100" aria-label={`Retirer ${s}`}>
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-cbm-muted pointer-events-none" />
          <input
            type="text"
            className="cbm-input pl-9"
            placeholder={atMax ? `Limite atteinte (${maxItems} max)` : (placeholder || "Rechercher...")}
            value={query}
            disabled={atMax}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          />
        </div>
        {open && !atMax && (filtered.length > 0 || (allowOther && !selected.includes("Autre"))) && (
          <div
            className="absolute z-10 left-0 right-0 mt-1 rounded-lg overflow-hidden max-h-56 overflow-y-auto"
            style={{ background: "#0F1E45", border: "1px solid rgba(184,196,224,0.16)", boxShadow: "0 8px 28px rgba(0,0,0,0.45)" }}
          >
            {filtered.map(o => (
              <button
                key={o}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); toggle(o); }}
                className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/5 transition-colors"
              >
                {o}
              </button>
            ))}
            {allowOther && !selected.includes("Autre") && (
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); toggle("Autre"); }}
                className="w-full text-left px-3 py-2 text-sm text-gold hover:bg-white/5 transition-colors border-t cbm-divider"
              >
                + Autre
              </button>
            )}
          </div>
        )}
      </div>
      {allowOther && selected.includes("Autre") && (
        <input
          type="text"
          className="cbm-input text-sm mt-2"
          placeholder="Précisez..."
          value={otherValue || ""}
          onChange={(e) => onOtherChange(e.target.value)}
        />
      )}
    </div>
  );
}

// ── (legacy free-text TagInput removed — expertises now use a curated MultiSelectPills) ──

function StatCard({ label, value, color = "text-gold" }) {
  return (
    <div className="cbm-stat-card text-center">
      <div className={`font-outfit font-bold text-2xl ${color}`}>{value}</div>
      <div className="text-cbm-muted text-xs mt-1">{label}</div>
    </div>
  );
}

const BENEFIT_TYPES = [
  { value: "service", label: "Service" },
  { value: "produit", label: "Produit" },
  { value: "outil", label: "Outil" },
  { value: "autre", label: "Autre" },
];

const BENEFIT_STATUS_LABEL = {
  pending: "En attente de validation",
  approved: "Publié",
  rejected: "Refusé",
};

const BENEFIT_STATUS_CLASS = {
  pending: "cbm-badge-new",
  approved: "cbm-badge-open",
  rejected: "cbm-badge-closed",
};

function BenefitSection({ hasProfile }) {
  const navigate = useNavigate();
  const [benefit, setBenefit] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/benefits/mine`, { withCredentials: true });
      setBenefit(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Scroll to section if URL hash is #mon-avantage
  useEffect(() => {
    if (!loading && window.location.hash === "#mon-avantage") {
      const el = document.getElementById("mon-avantage");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [loading]);

  const remove = async () => {
    if (!window.confirm("Supprimer cet avantage ? Vous pourrez en proposer un nouveau ensuite.")) return;
    try {
      await axios.delete(`${API}/api/benefits/mine`, { withCredentials: true });
      setBenefit(null);
    } catch (e) {
      alert("Erreur lors de la suppression");
    }
  };

  const resubmit = async () => {
    // Clean the rejected record and go to creation page
    try {
      await axios.delete(`${API}/api/benefits/mine`, { withCredentials: true });
    } catch (e) { /* ignore */ }
    navigate("/avantages/new");
  };

  if (!hasProfile) return null;
  if (loading) return (
    <div id="mon-avantage" className="mb-4 animate-fade-in">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-outfit font-semibold text-white text-base">Mes avantages</h2>
      </div>
      <div className="cbm-card p-4">
        <div className="cbm-skeleton h-16 rounded" />
      </div>
    </div>
  );

  return (
    <div id="mon-avantage" className="mb-4 animate-fade-in-up-delay-3" data-testid="my-benefit-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-outfit font-semibold text-white text-base">Mes avantages</h2>
        {!benefit && (
          <button
            data-testid="create-benefit-button"
            className="cbm-btn-primary text-xs py-1.5 px-3"
            onClick={() => navigate("/avantages/new")}
          >
            <Plus size={13} /> Nouveau
          </button>
        )}
      </div>

      <div className="cbm-card p-5">
        {/* Empty state */}
        {!benefit && (
          <div className="text-center py-4" data-testid="no-benefit-cta">
            <Gift size={24} className="text-cbm-muted mx-auto mb-2" />
            <p className="text-cbm-secondary text-sm mb-4 max-w-md mx-auto">
              Proposez un avantage exclusif aux membres pour gagner en visibilité et créer des opportunités.
            </p>
            <button
              className="cbm-btn-primary text-sm"
              onClick={() => navigate("/avantages/new")}
            >
              <Plus size={14} /> Créer mon avantage
            </button>
          </div>
        )}

        {/* Existing benefit display */}
        {benefit && (
          <div data-testid="my-benefit-view">
            {benefit.status === "rejected" && (
              <div className="bg-red-900/15 border border-red-800/30 text-red-300 rounded-lg px-3 py-2.5 text-sm mb-3" data-testid="benefit-rejected-notice">
                Votre avantage a été refusé. Vous pouvez le modifier et le soumettre à nouveau.
              </div>
            )}
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`cbm-badge ${BENEFIT_STATUS_CLASS[benefit.status]}`} data-testid="my-benefit-status">
                  {BENEFIT_STATUS_LABEL[benefit.status]}
                </span>
                <span className="cbm-badge cbm-badge-verified">{BENEFIT_TYPES.find(t => t.value === benefit.type)?.label || benefit.type}</span>
              </div>
            </div>
            <div className="mb-2">
              <span
                className="inline-block px-3 py-1 rounded-md font-outfit font-bold text-lg"
                style={{
                  background: "linear-gradient(135deg, rgba(201,162,39,0.2), rgba(201,162,39,0.08))",
                  border: "1px solid rgba(201,162,39,0.35)",
                  color: "#DDC168",
                }}
                data-testid="my-benefit-value"
              >
                {benefit.value}
              </span>
            </div>
            <h3 className="font-outfit font-semibold text-white mb-1" data-testid="my-benefit-title">{benefit.title}</h3>
            <p className="text-cbm-secondary text-sm whitespace-pre-line mb-2">{benefit.description}</p>
            {benefit.conditions && (
              <p className="text-cbm-muted text-xs italic border-l-2 border-gold/30 pl-2 mb-3">{benefit.conditions}</p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {benefit.status === "rejected" && (
                <button
                  data-testid="resubmit-benefit-button"
                  className="cbm-btn-primary text-xs py-1.5 px-3"
                  onClick={resubmit}
                >
                  <Plus size={12} /> Modifier et soumettre à nouveau
                </button>
              )}
              <button
                data-testid="delete-my-benefit"
                className="cbm-btn-ghost text-xs py-1.5 px-3 text-red-400 hover:text-red-300 hover:bg-red-900/10 border border-red-800/30"
                onClick={remove}
              >
                <Trash2 size={12} /> Supprimer
              </button>
            </div>
            <p className="text-cbm-muted text-xs mt-3">
              Limitation : 1 avantage actif par membre.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MonProfil() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pendingReviews, setPendingReviews] = useState([]);
  const [processingReviewId, setProcessingReviewId] = useState(null);
  const [showExpertiseSuggestion, setShowExpertiseSuggestion] = useState(false);
  const fileRef = useRef();

  // Live profile completion: while editing, mirror the backend formula on the
  // current form state (incl. pending photo). Otherwise show the saved value.
  const livePct = useMemo(
    () => editing ? computeCompletion(form || {}, { hasPendingPhoto: !!photoFile }) : (profile?.completion_pct ?? 0),
    [editing, form, photoFile, profile?.completion_pct]
  );

  const loadPendingReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/api/reviews/received/pending`, { withCredentials: true });
      setPendingReviews(data.items || []);
    } catch {
      setPendingReviews([]);
    }
  };

  const handleReviewAction = async (reviewId, action) => {
    setProcessingReviewId(reviewId);
    try {
      await axios.post(`${API}/api/reviews/${reviewId}/${action}`, {}, { withCredentials: true });
      setPendingReviews((prev) => prev.filter((r) => r._id !== reviewId));
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur");
    } finally {
      setProcessingReviewId(null);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, oRes] = await Promise.all([
          axios.get(`${API}/api/profiles/me`, { withCredentials: true }),
          axios.get(`${API}/api/opportunities/mine`, { withCredentials: true }),
        ]);
        setProfile(pRes.data);
        setOpps(oRes.data);
        if (pRes.data) setForm(pRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
    loadPendingReviews();
  }, []);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      let photoUrl = form.photo;
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        const { data: upload } = await axios.post(`${API}/api/upload/photo`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        photoUrl = upload.url;
      }
      const payload = { ...form, photo: photoUrl };
      let res;
      if (profile) {
        res = await axios.put(`${API}/api/profiles/me`, payload, { withCredentials: true });
      } else {
        res = await axios.post(`${API}/api/profiles/me`, payload, { withCredentials: true });
      }
      setProfile(res.data);
      setForm(res.data);
      setEditing(false);
      setSuccess("Profil sauvegardé !");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const deleteOpp = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try {
      await axios.delete(`${API}/api/opportunities/${id}`, { withCredentials: true });
      setOpps(prev => prev.filter(o => o._id !== id));
    } catch (e) {
      alert("Erreur lors de la suppression");
    }
  };

  const toggleOppStatus = async (opp) => {
    const newStatus = opp.statut === "ouvert" ? "cloture" : "ouvert";
    try {
      const { data } = await axios.put(`${API}/api/opportunities/${opp._id}`, { statut: newStatus }, { withCredentials: true });
      setOpps(prev => prev.map(o => o._id === opp._id ? data : o));
    } catch (e) {
      alert("Erreur");
    }
  };

  const toggleOppResolved = async (opp) => {
    try {
      const { data } = await axios.patch(
        `${API}/api/opportunities/${opp._id}/resolve`,
        { is_resolved: !opp.is_resolved },
        { withCredentials: true }
      );
      setOpps(prev => prev.map(o => o._id === opp._id ? data : o));
    } catch (e) {
      alert("Erreur");
    }
  };

  if (loading) return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="cbm-skeleton h-8 w-48 mb-6 rounded" />
      <div className="cbm-card p-6 space-y-4">
        <div className="cbm-skeleton rounded-full" style={{ width: 80, height: 80 }} />
        <div className="cbm-skeleton h-4 w-48" />
        <div className="cbm-skeleton h-4 w-64" />
      </div>
    </div>
  );

  const avatarSrc = photoPreview || (profile?.photo ? `${API}${profile.photo}` : null);
  const initials = ((form.nom_prenom || user?.name || user?.email || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase());
  const openOpps = opps.filter(o => o.statut === "ouvert");
  const closedOpps = opps.filter(o => o.statut === "cloture");
  const restantes = (profile?.annonces_max || 4) - (profile?.annonces_utilisees || 0);

  // No profile — create form
  if (!profile && !editing) {
    return (
      <div className="p-6 max-w-xl mx-auto animate-fade-in">
        <h1 className="cbm-section-title mb-2">Mon Profil</h1>
        <p className="text-cbm-muted text-sm mb-6">Créez votre profil pour apparaître dans l'annuaire.</p>
        <div className="cbm-card p-6 text-center">
          <div className="cbm-avatar mx-auto mb-4 text-gold font-outfit font-bold text-xl" style={{ width: 72, height: 72, background: "linear-gradient(135deg, #132040, #0D1B3E)" }}>
            {initials}
          </div>
          <p className="text-white font-outfit font-semibold mb-1">{user?.name || user?.email}</p>
          <p className="text-cbm-muted text-sm mb-5">Votre profil n'est pas encore créé</p>
          <button data-testid="create-profile-button" className="cbm-btn-primary" onClick={() => { setEditing(true); setForm({ nom_prenom: user?.name || "", email: user?.email || "" }); }}>
            <Plus size={16} /> Créer mon profil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 animate-fade-in-up">
        <h1 className="cbm-section-title">Mon Profil</h1>
        <div className="flex gap-2">
          {!editing ? (
            <button data-testid="edit-profile-button" className="cbm-btn-primary text-sm" onClick={() => setEditing(true)}>
              <Edit2 size={14} /> Modifier
            </button>
          ) : (
            <>
              <button data-testid="cancel-edit-button" className="cbm-btn-ghost text-sm" onClick={() => { setEditing(false); setForm(profile); setPhotoPreview(null); setPhotoFile(null); }}>
                <X size={14} /> Annuler
              </button>
              <button data-testid="save-profile-button" className="cbm-btn-primary text-sm" onClick={handleSave} disabled={saving}>
                {saving ? <><span className="w-4 h-4 border-2 border-navy border-t-transparent rounded-full animate-spin" />Saving...</> : <><Save size={14} /> Sauvegarder</>}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Pending reviews awaiting validation */}
      {pendingReviews.length > 0 && (
        <div className="mb-6" data-testid="pending-reviews-section">
          <div className="flex items-center gap-2 mb-3">
            <Star size={18} className="text-gold" />
            <h2 className="font-outfit font-semibold text-white text-base">
              Avis reçus à valider
              <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-gold text-navy-darker">
                {pendingReviews.length}
              </span>
            </h2>
          </div>
          <p className="text-cbm-muted text-xs mb-3">
            Ces avis sont masqués tant que vous ne les acceptez pas. Vous seul(e) choisissez ce qui apparaît sur votre profil.
          </p>
          <div className="space-y-3">
            {pendingReviews.map((r) => (
              <div key={r._id} className="cbm-card p-4" data-testid={`pending-review-${r._id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <p className="text-white text-sm font-semibold">
                      {r.author?.nom_prenom || r.author_name || "Membre"}
                    </p>
                    <p className="text-cbm-muted text-xs">
                      {r.author?.entreprise || "—"}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        size={14}
                        className={n <= (r.rating || 0) ? "text-gold fill-gold" : "text-cbm-muted opacity-30"}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-cbm-secondary text-sm leading-relaxed whitespace-pre-line mb-3">
                  {r.comment}
                </p>
                <p className="text-cbm-muted text-xs mb-3">
                  {new Date(r.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    data-testid={`approve-review-${r._id}`}
                    onClick={() => handleReviewAction(r._id, "approve")}
                    disabled={processingReviewId === r._id}
                    className="cbm-btn-primary text-xs py-2 px-3 disabled:opacity-60"
                  >
                    <CheckCircle2 size={13} /> Accepter
                  </button>
                  <button
                    data-testid={`reject-review-${r._id}`}
                    onClick={() => handleReviewAction(r._id, "reject")}
                    disabled={processingReviewId === r._id}
                    className="text-xs py-2 px-3 rounded-lg font-medium transition-all inline-flex items-center gap-1.5 disabled:opacity-60"
                    style={{ background: "rgba(220,38,38,0.1)", color: "#FCA5A5", border: "1px solid rgba(220,38,38,0.3)" }}
                  >
                    <X size={13} /> Refuser
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {success && (
        <div
          className="rounded-lg px-4 py-3 text-sm mb-4 animate-fade-in flex items-center gap-2 font-semibold"
          style={{
            background: "#D1FAE5",
            border: "1px solid #22c55e",
            color: "#0a1128",
          }}
          data-testid="success-message"
        >
          <CheckCircle2 size={16} style={{ color: "#15803d" }} />
          {success}
        </div>
      )}
      {error && <div className="bg-red-900/20 border border-red-800/30 text-red-400 rounded-lg px-4 py-2.5 text-sm mb-4" data-testid="error-message">{error}</div>}

      {/* Progression profil (complétion + état validation) */}
      {profile && (() => {
        const pct = livePct;
        const label = PROGRESSION_LABEL(pct);
        const pendingReview = !!profile.needs_review;
        const reached = pct >= 80 && !pendingReview;
        let color, badgeText, badgeClass, messageNode;
        if (pendingReview) {
          color = "#C9A227";
          badgeText = "En attente de validation";
          badgeClass = "cbm-badge-new";
          messageNode = (
            <span>
              Votre profil est <b className="text-gold">en cours de validation</b> par l'équipe. Une fois validé, il sera visible dans l'annuaire.
            </span>
          );
        } else if (reached) {
          color = "#22c55e";
        } else {
          color = pct >= 50 ? "#C9A227" : "#f97316";
          badgeText = "Non visible dans l'annuaire";
          badgeClass = "cbm-badge-new";
          messageNode = (
            <span>
              Votre profil doit être complété à <b className="text-gold">80% minimum</b> pour apparaître dans l'annuaire.
            </span>
          );
        }
        return (
          <div
            className="cbm-card p-4 mb-5 animate-fade-in-up"
            data-testid="profile-progression"
            style={{ borderLeftWidth: "3px", borderLeftColor: color }}
          >
            <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
              <div className="min-w-0">
                <p className="text-white text-sm font-medium">
                  Profil complété à <span className="font-outfit font-bold" style={{ color }} data-testid="profile-completion-pct">{pct}%</span>
                </p>
                <p className="text-cbm-secondary text-xs mt-0.5" data-testid="profile-progression-label" style={{ color }}>{label}</p>
              </div>
              {reached ? (
                <span className="cbm-badge cbm-badge-open whitespace-nowrap" data-testid="profile-visibility-badge">Visible dans l'annuaire</span>
              ) : (
                <span className={`cbm-badge ${badgeClass} whitespace-nowrap`} data-testid="profile-visibility-badge">{badgeText}</span>
              )}
            </div>
            <div className="h-2 rounded-full overflow-hidden mb-2" style={{ background: "rgba(184,196,224,0.12)" }}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{ width: `${pct}%`, background: color, boxShadow: `0 0 8px ${color}66` }}
                data-testid="profile-progression-bar"
              />
            </div>
            {!reached && messageNode && (
              <p
                className="text-sm mt-1 font-medium flex items-start gap-1.5"
                style={{ color: "#E5ECF8" }}
                data-testid="profile-progression-warning"
              >
                <Sparkles size={13} className="mt-0.5 flex-shrink-0 text-gold" />
                {messageNode}
              </p>
            )}
            <p className="text-cbm-secondary text-xs mt-2" data-testid="profile-progression-helper">
              Complétez votre profil pour améliorer votre visibilité dans l'annuaire et recevoir des mises en relation plus pertinentes.
            </p>
          </div>
        );
      })()}

      {/* Stats */}
      {profile && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5 animate-fade-in-up-delay-1">
          <StatCard label="Annonces publiées" value={opps.length} />
          <StatCard label="Annonces ouvertes" value={openOpps.length} color="text-green-400" />
          <StatCard label="Annonces clôturées" value={closedOpps.length} color="text-cbm-muted" />
          <StatCard label="Restantes ce mois" value={Math.max(0, restantes)} color={restantes <= 1 ? "text-red-400" : "text-gold"} />
        </div>
      )}

      {/* Profile info / edit form */}
      <div className="space-y-5 mb-4 animate-fade-in-up-delay-2">
        {/* Photo + meta header card */}
        <div className="cbm-card p-5 sm:p-6">
        <div className="flex items-start gap-5">
          <div className="relative">
            {avatarSrc ? (
              <img src={avatarSrc} alt="photo" className="cbm-photo-preview" style={{ width: 72, height: 72 }} />
            ) : (
              <div className="cbm-avatar text-gold font-outfit font-bold text-xl" style={{ width: 72, height: 72, background: "linear-gradient(135deg, #132040, #0D1B3E)" }}>{initials}</div>
            )}
            {editing && (
              <button className="absolute -bottom-1 -right-1 cbm-btn-primary w-7 h-7 p-0 rounded-full justify-center" onClick={() => fileRef.current?.click()} data-testid="upload-photo-button">
                <Camera size={13} />
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} data-testid="photo-input" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {profile?.is_verified && <CheckCircle2 size={16} className="text-green-400" />}
              {profile?.is_featured && <span className="cbm-badge cbm-badge-featured"><Star size={9} className="mr-0.5" />À la une</span>}
              {profile?.is_recommended && <span className="cbm-badge cbm-badge-recommended"><Sparkles size={9} className="mr-0.5" />Recommandé</span>}
              {profile?.is_new && <span className="cbm-badge cbm-badge-new">Nouveau</span>}
            </div>
            {profile?.annonces_reset_date && (
              <div className="flex items-center gap-1 text-cbm-muted text-xs mt-2">
                <Calendar size={12} />
                Réinitialisation le {new Date(profile.annonces_reset_date).toLocaleDateString("fr-FR")}
              </div>
            )}
            {profile?.last_active_at && (
              <div className="flex items-center gap-1 text-cbm-muted text-xs mt-1">
                <RefreshCw size={12} />
                Actif le {new Date(profile.last_active_at).toLocaleDateString("fr-FR")}
              </div>
            )}
          </div>
        </div>
        </div>

        {editing ? (
          /* ── Edit Form: 1 section = 1 card, with breathing space between ── */
          <>
            {/* 1. Informations principales */}
            <SectionCard
              icon={User}
              title="Informations principales"
              helper="Les informations visibles en priorité dans l'annuaire."
              testId="section-informations"
            >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-1">
              <div>
                <label className="cbm-label block mb-1.5">Nom & Prénom *</label>
                <input data-testid="field-nom_prenom" className="cbm-input" placeholder="Votre nom complet" value={form.nom_prenom || ""} onChange={(e) => setForm(p => ({ ...p, nom_prenom: e.target.value }))} />
              </div>
              <div>
                <label className="cbm-label block mb-1.5">Entreprise</label>
                <input data-testid="field-entreprise" className="cbm-input" placeholder="Nom de votre entreprise" value={form.entreprise || ""} onChange={(e) => setForm(p => ({ ...p, entreprise: e.target.value }))} />
              </div>
              <div>
                <label className="cbm-label block mb-1.5">Fonction *</label>
                <select
                  data-testid="field-fonction"
                  className="cbm-input"
                  value={form.fonction || ""}
                  onChange={(e) => setForm(p => ({ ...p, fonction: e.target.value }))}
                  required
                >
                  <option value="">Sélectionner</option>
                  {FONCTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                {form.fonction === "Autre" && (
                  <p
                    className="mt-1.5 text-xs font-medium flex items-start gap-1.5"
                    style={{ color: "#C9A227" }}
                    data-testid="fonction-other-notice"
                  >
                    <Sparkles size={12} className="mt-0.5 flex-shrink-0" />
                    Le club est réservé aux décideurs et entrepreneurs. Votre profil sera validé avant activation.
                  </p>
                )}
              </div>
              <div>
                <label className="cbm-label block mb-1.5">Secteur</label>
                <select data-testid="field-secteur" className="cbm-input" value={form.secteur || ""} onChange={(e) => setForm(p => ({ ...p, secteur: e.target.value }))}>
                  <option value="">Sélectionner</option>
                  {SECTEURS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="cbm-label block mb-1.5">Ville</label>
                <select data-testid="field-ville" className="cbm-input" value={form.ville || ""} onChange={(e) => setForm(p => ({ ...p, ville: e.target.value }))}>
                  <option value="">Sélectionner</option>
                  {VILLES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="cbm-label block mb-1.5">Taille de l'entreprise</label>
                <select data-testid="field-taille_entreprise" className="cbm-input" value={form.taille_entreprise || ""} onChange={(e) => setForm(p => ({ ...p, taille_entreprise: e.target.value }))}>
                  <option value="">Sélectionner</option>
                  {TAILLES_ENTREPRISE.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            </SectionCard>

            {/* 2. Présentation business */}
            <SectionCard
              icon={Briefcase}
              title="Présentation business"
              helper="Expliquez rapidement votre activité et la valeur que vous apportez aux membres."
              testId="section-presentation"
            >
            <div className="space-y-4 pb-1">
              <div>
                <label className="cbm-label block mb-1.5">Ce que je propose (offre)</label>
                <textarea data-testid="field-offre" className="cbm-input" rows={3} placeholder="Décrivez votre offre..." value={form.offre || ""} onChange={(e) => setForm(p => ({ ...p, offre: e.target.value }))} />
              </div>
              <div>
                <label className="cbm-label block mb-1.5">Je peux aider avec</label>
                <textarea data-testid="field-aide" className="cbm-input" rows={2} placeholder="Dans quel domaine pouvez-vous aider ?" value={form.je_peux_aider || ""} onChange={(e) => setForm(p => ({ ...p, je_peux_aider: e.target.value }))} />
              </div>
              <div>
                <label className="cbm-label block mb-1.5">Références (clients, collaborations)</label>
                <textarea data-testid="field-references" className="cbm-input" rows={2} placeholder="Listez quelques clients ou collaborations notables..." value={form.references || ""} onChange={(e) => setForm(p => ({ ...p, references: e.target.value }))} />
              </div>
            </div>
            </SectionCard>

            {/* 3. Objectifs & networking */}
            <SectionCard
              icon={Target}
              title="Objectifs & networking"
              helper="Ces informations permettent d'améliorer les mises en relation et le matching dans le club."
              testId="section-objectifs"
            >
            <div className="space-y-5 pb-1">
              <div>
                <label className="cbm-label block mb-2.5">Pourquoi rejoignez-vous le club ?</label>
                <div className="flex flex-wrap gap-2.5" data-testid="field-objectifs">
                  {OBJECTIFS_OPTIONS.map(opt => {
                    const active = (form.objectifs || []).includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => {
                          const current = form.objectifs || [];
                          setForm(p => ({
                            ...p,
                            objectifs: active ? current.filter(x => x !== opt) : [...current, opt],
                          }));
                        }}
                        className={`text-xs font-medium px-3 py-1.5 rounded-full transition-all duration-150 ${active ? "cbm-pill-active" : "cbm-pill-idle"}`}
                      >
                        {active && <CheckCircle2 size={11} className="inline -mt-0.5 mr-1" />}
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="cbm-label block mb-1.5">Disponible pour échanger ?</label>
                  <select data-testid="field-disponibilite" className="cbm-input" value={form.disponibilite || ""} onChange={(e) => setForm(p => ({ ...p, disponibilite: e.target.value }))}>
                    <option value="">Sélectionner</option>
                    {DISPONIBILITES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="cbm-label block mb-1.5">Zone d'activité</label>
                  <select data-testid="field-zone_activite" className="cbm-input" value={form.zone_activite || ""} onChange={(e) => setForm(p => ({ ...p, zone_activite: e.target.value }))}>
                    <option value="">Sélectionner</option>
                    {ZONES_ACTIVITE.map(z => <option key={z} value={z}>{z}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
                  <label className="cbm-label">
                    Expertises / compétences principales
                  </label>
                  <span className="text-cbm-secondary text-xs" data-testid="expertises-counter">
                    {(form.expertises || []).length}/{MAX_EXPERTISES} sélectionnées
                  </span>
                </div>
                <MultiSelectPills
                  testId="field-expertises"
                  options={EXPERTISES_OPTIONS}
                  value={form.expertises || []}
                  onChange={(v) => setForm(p => ({ ...p, expertises: v }))}
                  placeholder="Rechercher une expertise…"
                  maxItems={MAX_EXPERTISES}
                />
                <div className="mt-2">
                  {!showExpertiseSuggestion ? (
                    <button
                      type="button"
                      onClick={() => setShowExpertiseSuggestion(true)}
                      className="text-xs text-gold hover:underline transition-colors"
                      data-testid="suggest-expertise-toggle"
                    >
                      Vous ne trouvez pas votre expertise&nbsp;?
                    </button>
                  ) : (
                    <div>
                      <input
                        data-testid="field-expertise_suggestion"
                        className="cbm-input text-sm"
                        placeholder="Suggérez une expertise — l'équipe l'examinera"
                        value={form.expertise_suggestion || ""}
                        onChange={(e) => setForm(p => ({ ...p, expertise_suggestion: e.target.value }))}
                      />
                      <p className="text-cbm-secondary text-xs mt-1">
                        Votre suggestion sera transmise à l'équipe pour ajout à la liste officielle.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="cbm-label block mb-2.5">Langues parlées</label>
                <div className="flex flex-wrap gap-2.5" data-testid="field-langues">
                  {LANGUES_OPTIONS.map(lang => {
                    const active = (form.langues || []).includes(lang);
                    return (
                      <button
                        key={lang}
                        type="button"
                        onClick={() => {
                          const current = form.langues || [];
                          setForm(p => ({
                            ...p,
                            langues: active ? current.filter(x => x !== lang) : [...current, lang],
                          }));
                        }}
                        className={`text-xs font-medium px-3 py-1.5 rounded-full transition-all duration-150 ${active ? "cbm-pill-active" : "cbm-pill-idle"}`}
                      >
                        {active && <CheckCircle2 size={11} className="inline -mt-0.5 mr-1" />}
                        {lang}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            </SectionCard>

            {/* 4. Liens & contact */}
            <SectionCard
              icon={LinkIcon}
              title="Liens & contact"
              helper="Ajoutez vos liens professionnels pour faciliter les mises en relation."
              testId="section-liens"
            >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-1">
              {[
                { key: "phone", label: "Téléphone", placeholder: "+212 6 00 00 00 00" },
                { key: "email", label: "Email contact", placeholder: "email@exemple.com" },
                { key: "linkedin_url", label: "LinkedIn", placeholder: "https://linkedin.com/in/..." },
                { key: "website", label: "Site web", placeholder: "https://..." },
                { key: "calendly_url", label: "Calendly", placeholder: "https://calendly.com/votre-lien" },
                { key: "brochure_url", label: "Plaquette commerciale", placeholder: "https://...", helper: "Lien vers votre présentation (Drive, PDF, Notion…)", type: "url" },
              ].map(({ key, label, placeholder, helper, type }) => (
                <div key={key}>
                  <label className="cbm-label block mb-1.5">{label}</label>
                  <input
                    data-testid={`field-${key}`}
                    className="cbm-input"
                    type={type || "text"}
                    placeholder={placeholder}
                    value={form[key] || ""}
                    onChange={(e) => setForm(p => ({ ...p, [key]: e.target.value }))}
                  />
                  {helper && <p className="text-cbm-secondary text-xs mt-1">{helper}</p>}
                </div>
              ))}
            </div>
            </SectionCard>

            {/* 5. Réseaux & organisations */}
            <SectionCard
              icon={Building2}
              title="Réseaux & organisations"
              helper="Indiquez les réseaux et organisations dont vous faites partie."
              testId="section-reseaux"
            >
            <div className="space-y-4 pb-1">
              <div>
                <label className="cbm-label block mb-1.5">Fédérations / organisations professionnelles</label>
                <MultiSelectPills
                  testId="field-federations"
                  options={FEDERATIONS}
                  value={form.federations || []}
                  onChange={(v) => setForm(p => ({ ...p, federations: v, ...(v.includes("Autre") ? {} : { other_federation: "" }) }))}
                  placeholder="Rechercher une fédération…"
                  allowOther
                  otherValue={form.other_federation}
                  onOtherChange={(val) => setForm(p => ({ ...p, other_federation: val }))}
                />
              </div>
              <div>
                <label className="cbm-label block mb-1.5">Clubs business / réseaux</label>
                <MultiSelectPills
                  testId="field-business_clubs"
                  options={BUSINESS_CLUBS}
                  value={form.business_clubs || []}
                  onChange={(v) => setForm(p => ({ ...p, business_clubs: v, ...(v.includes("Autre") ? {} : { other_business_club: "" }) }))}
                  placeholder="Rechercher un club…"
                  allowOther
                  otherValue={form.other_business_club}
                  onOtherChange={(val) => setForm(p => ({ ...p, other_business_club: val }))}
                />
              </div>
            </div>
            </SectionCard>

            {/* 6. Vérification entreprise */}
            <SectionCard
              icon={Shield}
              title="Vérification entreprise"
              helper="Ajoutez vos informations professionnelles pour renforcer la confiance et la crédibilité de votre profil."
              testId="section-verification"
            >
            <div className="pb-1">
              <label className="cbm-label block mb-1.5">ICE / Patente / Identifiant entreprise</label>
              <input
                data-testid="field-ice_patente"
                className="cbm-input"
                placeholder="Ex : 002345678000012"
                value={form.ice_patente || ""}
                onChange={(e) => setForm(p => ({ ...p, ice_patente: e.target.value }))}
              />
              <p className="text-cbm-secondary text-xs mt-2 flex items-start gap-1.5">
                <Shield size={11} className="mt-0.5 flex-shrink-0" />
                Information privée — visible uniquement par l'équipe pour valider le badge "Entreprise vérifiée".
              </p>
              {form.company_verification_status === "pending" && (
                <p className="text-xs mt-2 font-medium" style={{ color: "#C9A227" }}>
                  En attente de validation par l'équipe.
                </p>
              )}
              {form.company_verification_status === "verified" && (
                <p className="text-xs mt-2 font-medium inline-flex items-center gap-1" style={{ color: "#22c55e" }}>
                  <CheckCircle2 size={12} /> Entreprise vérifiée.
                </p>
              )}
              {form.company_verification_status === "rejected" && (
                <p className="text-xs mt-2 font-medium" style={{ color: "#f97316" }}>
                  Vérification refusée. Mettez à jour votre identifiant pour soumettre à nouveau.
                </p>
              )}
            </div>
            </SectionCard>

            {/* 7. Confidentialité */}
            <SectionCard
              icon={SettingsIcon}
              title="Confidentialité"
              helper="Choisissez les informations visibles publiquement dans l'annuaire."
              testId="section-confidentialite"
            >
            <div className="flex gap-5 items-center flex-wrap pb-1">
              <label className="flex items-center gap-2 text-cbm-secondary text-sm cursor-pointer">
                <input type="checkbox" checked={form.email_visible !== false} onChange={(e) => setForm(p => ({ ...p, email_visible: e.target.checked }))} className="accent-gold" />
                Email visible
              </label>
              <label className="flex items-center gap-2 text-cbm-secondary text-sm cursor-pointer">
                <input type="checkbox" checked={form.phone_visible !== false} onChange={(e) => setForm(p => ({ ...p, phone_visible: e.target.checked }))} className="accent-gold" />
                Téléphone visible
              </label>
            </div>
            </SectionCard>
          </>
        ) : (
          /* View mode */
          <div className="cbm-card p-5 sm:p-6">
          <div className="space-y-4">
            <div>
              <h2 className="font-outfit font-bold text-white text-xl">{profile?.nom_prenom}</h2>
              {profile?.fonction && <p className="text-white/90 text-sm mt-0.5" data-testid="profile-fonction">{profile.fonction}</p>}
              {profile?.entreprise && <p className="text-gold font-medium mt-0.5">{profile.entreprise}</p>}
              <div className="flex gap-3 mt-2 flex-wrap text-sm text-cbm-muted">
                {profile?.secteur && <span>{profile.secteur}</span>}
                {profile?.ville && <span>{profile.ville}</span>}
              </div>
            </div>
            {profile?.offre && (
              <div>
                <p className="cbm-label mb-1">Ce que je propose</p>
                <p className="text-cbm-secondary text-sm">{profile.offre}</p>
              </div>
            )}
            {profile?.je_peux_aider && (
              <div>
                <p className="cbm-label mb-1">Je peux aider avec</p>
                <p className="text-cbm-secondary text-sm">{profile.je_peux_aider}</p>
              </div>
            )}
            {profile?.references && (
              <div>
                <p className="cbm-label mb-1">Références</p>
                <p className="text-cbm-secondary text-sm whitespace-pre-line">{profile.references}</p>
              </div>
            )}
            <div className="flex gap-2 flex-wrap pt-1">
              {profile?.linkedin_url && <a href={profile.linkedin_url} target="_blank" rel="noreferrer" className="cbm-btn-ghost text-xs"><Linkedin size={13} /> LinkedIn</a>}
              {profile?.website && <a href={profile.website} target="_blank" rel="noreferrer" className="cbm-btn-ghost text-xs"><Globe size={13} /> Site web</a>}
              {profile?.calendly_url && <a href={profile.calendly_url} target="_blank" rel="noreferrer" className="cbm-btn-secondary text-xs" data-testid="my-profile-booking-link"><Calendar size={13} /> Prendre rendez-vous</a>}
              {profile?.phone_visible && profile?.phone && <span className="cbm-btn-ghost text-xs cursor-default"><Phone size={13} /> {profile.phone}</span>}
            </div>
          </div>
          </div>
        )}
      </div>

      {/* My opportunities — hidden in edit mode (focus on profile editing) */}
      {!editing && (
      <div className="animate-fade-in-up-delay-2 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-outfit font-semibold text-white text-base">Mes annonces</h2>
          <button
            data-testid="new-opportunity-button"
            className="cbm-btn-primary text-xs py-1.5 px-3"
            onClick={() => navigate("/poster-annonce")}
          >
            <Plus size={13} /> Nouvelle
          </button>
        </div>
        {opps.length === 0 ? (
          <div className="cbm-card p-8 text-center">
            <MessageCircle size={28} className="text-cbm-muted mx-auto mb-2" />
            <p className="text-cbm-secondary text-sm">Aucune annonce publiée</p>
            <button className="cbm-btn-primary mt-3 text-sm" onClick={() => navigate("/poster-annonce")} data-testid="post-first-opportunity">
              <Plus size={14} /> Poster ma première annonce
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {opps.map(opp => (
              <div key={opp._id} className="cbm-card p-4 flex items-start gap-3" data-testid={`my-opp-${opp._id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap mb-1">
                    <span className={`cbm-badge ${opp.type === "cherche" ? "cbm-badge-type-cherche" : opp.type === "propose" ? "cbm-badge-type-propose" : "cbm-badge-type-opportunite"}`}>
                      {TYPE_LABELS[opp.type] || opp.type}
                    </span>
                    <span className={`cbm-badge ${opp.statut === "ouvert" ? "cbm-badge-open" : "cbm-badge-closed"}`}>
                      {opp.statut === "ouvert" ? "Ouvert" : "Clôturé"}
                    </span>
                    {opp.type === "cherche" && opp.is_resolved && (
                      <span className="cbm-badge" style={{ background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.45)", color: "#22c55e" }} data-testid={`opp-resolved-badge-${opp._id}`}>
                        <CheckCircle2 size={9} /> Trouvé
                      </span>
                    )}
                    {opp.is_featured && <span className="cbm-badge cbm-badge-featured"><Star size={9} />À la une</span>}
                    {opp.is_priority && <span className="cbm-badge cbm-badge-priority"><Flame size={9} />Prioritaire</span>}
                  </div>
                  <p className="text-white text-sm font-medium">{opp.titre}</p>
                  {opp.description && <p className="text-cbm-muted text-xs mt-0.5 line-clamp-1">{opp.description}</p>}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {opp.type === "cherche" && (
                    <button
                      data-testid={`toggle-opp-resolved-${opp._id}`}
                      className="cbm-btn-ghost text-xs py-1 px-2 border"
                      style={{
                        borderColor: opp.is_resolved ? "rgba(34,197,94,0.45)" : "rgba(201,162,39,0.35)",
                        color: opp.is_resolved ? "#22c55e" : "#C9A227",
                      }}
                      onClick={() => toggleOppResolved(opp)}
                      title={opp.is_resolved ? "Marquer comme non trouvé" : "Marquer comme trouvé"}
                    >
                      {opp.is_resolved ? "✓ Trouvé" : "Marquer trouvé"}
                    </button>
                  )}
                  <button
                    data-testid={`toggle-opp-status-${opp._id}`}
                    className="cbm-btn-ghost text-xs py-1 px-2 border border-white/10"
                    onClick={() => toggleOppStatus(opp)}
                    title={opp.statut === "ouvert" ? "Clôturer" : "Rouvrir"}
                  >
                    {opp.statut === "ouvert" ? "Clôturer" : "Rouvrir"}
                  </button>
                  <button
                    data-testid={`delete-opp-${opp._id}`}
                    className="cbm-btn-ghost text-xs py-1 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/10"
                    onClick={() => deleteOpp(opp._id)}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* My benefit — placé APRÈS Mes annonces, masqué en mode édition */}
      {!editing && <BenefitSection hasProfile={!!profile} />}
    </div>
  );
}
